import { workspaceFilesHref } from '@/lib/workspace-files-route';

export type WorkspaceFilesCreationKind = 'avatar' | 'brand';

type DirectoryKind = 'directory' | 'file';

function withoutTrailingSlash(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

export function getWorkspaceFilesCreationKind(args: {
  orgSlug: string;
  homePath: string | null;
  entryPath: string;
  entryKind: DirectoryKind;
}): WorkspaceFilesCreationKind | null {
  if (args.entryKind !== 'directory' || !args.homePath) return null;
  const entryPath = withoutTrailingSlash(args.entryPath);
  const homePath = withoutTrailingSlash(args.homePath);

  if (entryPath === `${homePath}/Avatars`) return 'avatar';
  if (entryPath === `/${args.orgSlug}/brands`) return 'brand';
  return null;
}

export function workspaceFilesEntryHref(args: {
  orgSlug: string;
  homePath: string | null;
  entryPath: string;
}): string {
  const kind = getWorkspaceFilesCreationKind({
    ...args,
    entryKind: 'directory',
  });
  if (kind === 'avatar') return `/${args.orgSlug}/files/avatars`;
  return workspaceFilesHref(args.orgSlug, args.entryPath);
}

export function writeWorkspaceFilesCreationDraft(
  storage: Pick<Storage, 'removeItem' | 'setItem'>,
  prompt: string
): boolean {
  try {
    for (const key of [
      'orgChatAutoStart',
      'orgChatPendingPrompt',
      'orgChatPendingRawUserPrompt',
      'orgChatPendingAttachments',
      'orgChatPendingPresets',
    ]) {
      storage.removeItem(key);
    }
    storage.setItem('orgChatPendingDraftPrompt', prompt);
    return true;
  } catch {
    return false;
  }
}

/**
 * Stage a request for a new chat that sends itself: `visiblePrompt` is what
 * the user sees in the thread, `prompt` is what the agent receives.
 */
export function stageNewChatRequest(
  storage: Pick<Storage, 'removeItem' | 'setItem'>,
  args: { prompt: string; visiblePrompt?: string }
): boolean {
  try {
    for (const key of [
      'orgChatPendingDraftPrompt',
      'orgChatPendingAttachments',
      'orgChatPendingPresets',
      'orgChatPendingRawUserPrompt',
    ]) {
      storage.removeItem(key);
    }
    const visible = args.visiblePrompt ?? args.prompt;
    storage.setItem('orgChatPendingPrompt', visible);
    if (visible !== args.prompt) {
      storage.setItem('orgChatPendingRawUserPrompt', args.prompt);
    }
    storage.setItem('orgChatAutoStart', '1');
    return true;
  } catch {
    return false;
  }
}
